import ButtonIconPicker from 'client/components/core/Sidebar/components/Settings/components/ButtonIconPicker'
import FontSize from 'client/components/core/Sidebar/components/Settings/components/Font/FontSize'
import TextFontUpdater from 'client/components/core/Sidebar/components/Settings/components/Font/TextFontUpdater'
import HoverButtonSetting from 'client/components/core/Sidebar/components/Settings/components/HoverButton'
import {
  useUpdateEntity,
  useUpdateProp,
} from 'client/components/core/Sidebar/components/Settings/hooks/useUpdateProps'
import DelayOption from 'client/components/core/Sidebar/components/Settings/options/DelayOption'
import DeviceAppearanceNew from 'client/components/core/Sidebar/components/Settings/options/DeviceAppearance/DeviceAppearanceNew'
import HtmlAttrId from 'client/components/core/Sidebar/components/Settings/options/HtmlAttrId'
import {
  FlexAlignStyle,
  ShadowStyle,
} from 'client/components/core/Sidebar/components/Settings/styles'
import ButtonWidth from 'client/components/core/Sidebar/components/Settings/styles/ButtonWidth'
import TextLineHeight from 'client/components/core/Sidebar/components/Settings/styles/TextLineHeight'
import { useDeviceModeContext } from 'client/context/DeviceModeContext'
import { ButtonInterface } from 'common/types/entities/button-interface'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useIsAllowedWebsiteHeaderEntity } from 'src/editor/hooks/use-is-allowed-website-header-entity'
import { GroupTitle } from 'src/editor/layout/sidebar/settings/group-title'
import { ColorPickerControl } from 'src/editor/shared/settings/controls/color-picker/color-picker-control'
import { TextInputControl } from 'src/editor/shared/settings/controls/text-input-control'
import { SizeAndPositionGroup } from 'src/editor/shared/settings/groups/size-and-position'
import { EntityBorderGroup } from 'src/editor/shared/settings/wrappers/border-groups/entity-border-group'
import { useCalculateButtonStyles } from '../hooks/use-calculate-button-styles'
import { WebsiteSettingsTypographyTypeEnum } from 'src/shared/types/website-settings-types'
import { getMobileOrDesktop } from 'src/shared/utils/get-mobile-or-desktop'

export const CommonButtonSettings = ({
  entity,
}: {
  entity: ButtonInterface
}) => {
  const { t } = useTranslation()
  const { isMobile } = useDeviceModeContext()
  const updateProp = useUpdateProp(entity)
  const updateEntity = useUpdateEntity<ButtonInterface>()
  const isAllowedWebsiteHeaderEntity = useIsAllowedWebsiteHeaderEntity(
    entity.parentId,
    entity.type,
  )

  const { calculatedBorder } = useCalculateButtonStyles({
    border: entity.border,
    mobileBorder: entity.mobileBorder,
    buttonType:
      entity.buttonType || WebsiteSettingsTypographyTypeEnum.PrimaryButton,
  })

  return (
    <>
      <FlexAlignStyle
        align={entity.alignSelf}
        mobileAlign={entity.mobileAlignSelf}
        update={updateProp('alignSelf')}
        mobileUpdate={updateProp('mobileAlignSelf')}
      />
      <ButtonWidth
        update={updateProp('width')}
        mobileUpdate={updateProp('mobileWidth')}
        width={entity.width}
        mobileWidth={entity.mobileWidth}
      />
      <SizeAndPositionGroup
        padding={isMobile ? entity?.mobilePadding : entity.padding}
        updatePadding={updateProp(isMobile ? 'mobilePadding' : 'padding')}
        margin={isMobile ? entity.mobileMargin : entity.margin}
        updateMargin={updateProp(isMobile ? 'mobileMargin' : 'margin')}
      />
      <GroupTitle>{t('typography')}</GroupTitle>
      <TextInputControl
        wrapperProps={{
          labelText: t('entity_settings.button.button_text.label'),
        }}
        value={entity.text}
        update={updateProp('text')}
      />
      <FontSize
        label="settings_styles.font_size.label"
        fontSize={entity.textFontSize}
        mobileFontSize={entity.mobileTextFontSize}
        onChange={updateProp('textFontSize')}
        onChangeMobile={updateProp('mobileTextFontSize')}
      />
      <TextLineHeight
        fontSize={entity.textFontSize}
        mobileFontSize={entity.mobileTextFontSize}
        update={updateProp('lineHeight')}
        mobileUpdate={updateProp('mobileLineHeight')}
        lineHeight={entity.lineHeight}
        mobileLineHeight={entity.mobileLineHeight}
        removeEntityStyle={updateProp('lineHeight')}
        removeEntityMobileStyle={updateProp('mobileLineHeight')}
      />
      <TextFontUpdater<ButtonInterface>
        entity={entity}
        update={updateEntity}
        fontFamilyKey={'textFontFamily'}
        fontWeightKey={'textFontWeight'}
        fontStyleKey={'textFontStyle'}
        mobileFontFamilyKey={'mobileTextFontFamily'}
        mobileFontWeightKey={'mobileTextFontWeight'}
        mobileFontStyleKey={'mobileTextFontStyle'}
        fontFileIdKey={'textFontFileId'}
        mobileFontFileIdKey={'mobileTextFontFileId'}
      />
      <TextInputControl
        wrapperProps={{
          labelText: 'entity_settings.button.sub_text.label',
        }}
        update={updateProp('subText')}
        value={entity.subText}
      />
      {entity.subText && (
        <FontSize
          label={'entity_settings.button.sub_text.font_size.label'}
          fontSize={entity.subTextFontSize}
          mobileFontSize={entity.mobileSubTextFontSize}
          onChange={updateProp('subTextFontSize')}
          onChangeMobile={updateProp('mobileSubTextFontSize')}
        />
      )}
      {entity.subText && (
        <TextFontUpdater
          entity={entity}
          update={updateEntity}
          fontFamilyKey={'subTextFontFamily'}
          fontWeightKey={'subTextFontWeight'}
          fontStyleKey={'subTextFontStyle'}
          mobileFontFamilyKey={'mobileSubTextFontFamily'}
          mobileFontWeightKey={'mobileSubTextFontWeight'}
          mobileFontStyleKey={'mobileSubTextFontStyle'}
        />
      )}
      <ButtonIconPicker
        valueBefore={entity.iconClassNameBefore}
        valueAfter={entity.iconClassNameAfter}
        updateBefore={updateProp('iconClassNameBefore')}
        updateAfter={updateProp('iconClassNameAfter')}
      />
      <GroupTitle>color</GroupTitle>
      <ColorPickerControl
        isGradientAvailable
        label="background color"
        update={updateProp('background')}
        mobileUpdate={updateProp('mobileBackground')}
        color={entity.background}
        mobileColor={entity.mobileBackground}
      />
      <ColorPickerControl
        label="text color"
        update={updateProp('textColor')}
        mobileUpdate={updateProp('mobileTextColor')}
        color={entity.textColor}
        mobileColor={entity.mobileTextColor}
      />
      {entity.subText && (
        <ColorPickerControl
          label="entity_settings.button.sub_text.color.label"
          update={updateProp('subTextColor')}
          mobileUpdate={updateProp('mobileSubTextColor')}
          color={entity.subTextColor}
          mobileColor={entity.mobileSubTextColor}
          isClearable
        />
      )}
      <EntityBorderGroup
        entity={entity}
        propertyKey="border"
        mobilePropertyKey="mobileBorder"
        calculateValuesHook={() => {
          return {
            calculatedBorder: getMobileOrDesktop(
              calculatedBorder.desktop,
              calculatedBorder.mobile,
              isMobile,
            ),
          }
        }}
      />

      <ShadowStyle
        shadow={entity.boxShadow}
        mobileShadow={entity.mobileBoxShadow}
        update={updateProp('boxShadow')}
        mobileUpdate={updateProp('mobileBoxShadow')}
      />
      <HoverButtonSetting
        textColor={entity.textColor}
        backgroundColor={entity.background ?? entity.backgroundColor}
        subTextColor={entity.subTextColor}
        update={updateProp('hover')}
        mobileUpdate={updateProp('mobileHover')}
        hover={entity.hover}
        mobileHover={entity.mobileHover}
      />
      <DelayOption update={updateProp('delay')} delay={entity.delay} />
      {!isAllowedWebsiteHeaderEntity && <DeviceAppearanceNew entity={entity} />}
      <HtmlAttrId attrId={entity.htmlAttrId} />
    </>
  )
}
